/**
 * Search Results Component
 *
 * Displays search results in a clean, Google-like layout without card borders.
 */

import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useHref } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { SearchResultItem, SearchResponse } from '../../types/search';
import { getSourceLabel } from '../../types/search';
import { getSourceFallbackIcon } from '../../utils/sourceIcons';
import { SUNRISE_SOURCE, sunriseTicketPath } from '../../utils/sunrise';
import { trackEvent } from '../../services/googleAnalytics';
import { isAdvancedQuery } from '../../searchCore/querySyntax';
import type { PhraseHintState } from '../../hooks/useSearchController';
import { StatusMessage } from './StatusMessage';
import { PhraseHint, QuerySyntaxSummary } from './QuerySyntax';

// -----------------------------------------------------------------------------
// Result Card (Google-style)
// -----------------------------------------------------------------------------

interface ResultCardProps {
  result: SearchResultItem;
  /** 1-based rank across all pages. */
  position: number;
  searchTerm: string;
  untitledText: string;
  openInNewTabText: string;
  ticketNumberText: (number: string) => string;
}

function ResultCard({
  result,
  position,
  searchTerm,
  untitledText,
  openInNewTabText,
  ticketNumberText,
}: ResultCardProps) {
  // Legacy Sunrise tickets open in-app; the sunrise-crm: link needs a rarely installed client.
  const ticketPath =
    result.source === SUNRISE_SOURCE
      ? sunriseTicketPath(result.parent_document_id ?? result.id)
      : null;
  const rawTicketNumber = result.source_fields?.ticket_number;
  const ticketNumber =
    ticketPath && (typeof rawTicketNumber === 'string' || typeof rawTicketNumber === 'number')
      ? ticketNumberText(String(rawTicketNumber))
      : null;
  const breadcrumb = result.breadcrumb?.filter(Boolean).join(' > ');

  const linkProps = ticketPath
    ? { component: RouterLink, to: ticketPath }
    : result.url
      ? { component: 'a' as const, href: result.url }
      : null;
  const ticketHref = useHref(ticketPath ?? '.');
  const newTabHref = ticketPath ? ticketHref : result.url;

  const trackClick = (newTab: boolean) =>
    trackEvent('select_content', {
      content_type: 'search_result',
      content_id: result.id,
      source: result.source,
      source_type: result.source_type,
      position,
      search_term: searchTerm,
      new_tab: newTab,
    });

  // Middle-click only fires `auxclick`; Ctrl/Cmd-click fires `click`.
  const handleClick = (event: React.MouseEvent) =>
    trackClick(event.ctrlKey || event.metaKey || event.shiftKey);
  const handleAuxClick = (event: React.MouseEvent) => {
    if (event.button === 1) trackClick(true);
  };

  return (
    <Box
      data-result-id={result.id}
      sx={{
        position: 'relative',
        '& .open-in-new-tab': { opacity: 0, transition: 'opacity 150ms' },
        '&:hover .open-in-new-tab, & .open-in-new-tab:focus-visible': { opacity: 1 },
        // Touch devices have no hover, so keep the button visible there
        '@media (hover: none)': { '& .open-in-new-tab': { opacity: 1 } },
      }}
    >
      <ButtonBase
        {...(linkProps ?? { component: 'div' as const })}
        disabled={!linkProps}
        onClick={linkProps ? handleClick : undefined}
        onAuxClick={linkProps ? handleAuxClick : undefined}
        sx={{
          display: 'block',
          textAlign: 'left',
          width: '100%',
          py: 2,
          px: 1,
          borderRadius: 0.5,
          color: 'inherit',
          textDecoration: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack spacing={1.25}>
          {/* Row 1: Thumbnail + source label + breadcrumb */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 5 }}>
            <Avatar
              variant="circular"
              src={getSourceFallbackIcon(result.source, result.source_type)}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, lineHeight: 1.2, display: 'block' }}
              >
                {getSourceLabel(result.source)}
              </Typography>
              {ticketNumber && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 500,
                  }}
                >
                  {ticketNumber}
                </Typography>
              )}
              {breadcrumb && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    lineHeight: 1.2,
                    fontWeight: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 500,
                  }}
                >
                  {breadcrumb}
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Row 2: Title */}
          <Typography
            variant="h3"
            component="h3"
            color="primary"
            sx={{
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {result.title || untitledText}
          </Typography>

          {/* Row 3: Summary */}
          {result.summary && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {result.summary.length > 250
                ? result.summary.substring(0, 250) + '...'
                : result.summary}
            </Typography>
          )}
        </Stack>
      </ButtonBase>

      {/* Sibling of the card link, since interactive elements must not be nested */}
      {newTabHref && (
        <Tooltip title={openInNewTabText}>
          <IconButton
            component="a"
            href={newTabHref}
            className="open-in-new-tab"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick(true)}
            onAuxClick={handleAuxClick}
            size="small"
            aria-label={openInNewTabText}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary' }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Loading Skeleton (Google-style)
// -----------------------------------------------------------------------------

function ResultSkeleton() {
  return (
    <Box sx={{ py: 2, px: 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Skeleton variant="rounded" width={40} height={40} />
        <Box>
          <Skeleton variant="text" width={100} height={16} />
          <Skeleton variant="text" width={220} height={14} />
        </Box>
      </Stack>
      <Skeleton variant="text" width="55%" height={26} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="100%" height={18} />
      <Skeleton variant="text" width="85%" height={18} />
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

interface SearchResultsProps {
  results: SearchResponse | null;
  isLoading?: boolean;
  query?: string;
  /** Size of the whole result set while keywords narrow it ("12 of 200 results"). */
  unnarrowedTotal?: number | null;
  /** Search again for a query derived from the results (condition removed, fix, exact phrase). */
  onSearchQuery?: (query: string) => void;
  /** Put text into the search bar and focus it ("+ Add word"). */
  onEditQuery?: (query: string) => void;
  phraseHint?: PhraseHintState | null;
}

export function SearchResults({
  results,
  isLoading = false,
  query,
  unnarrowedTotal,
  onSearchQuery,
  onEditQuery,
  phraseHint,
}: SearchResultsProps) {
  const { t } = useTranslation('search');

  // Loading state
  if (isLoading) {
    return (
      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <ResultSkeleton key={i} />
        ))}
      </Stack>
    );
  }

  // No search yet
  if (!results && !query) {
    return null;
  }

  if (!results) return null;

  const total = results.total.toLocaleString();
  const advanced = isAdvancedQuery(results.query);
  // Advanced queries read "6 results match [chips]" (concept §5.3).
  const countHtml = advanced
    ? unnarrowedTotal != null
      ? t('syntax.matchesNarrowed', { total, all: unnarrowedTotal.toLocaleString() })
      : t('syntax.matches', { count: results.total, total })
    : unnarrowedTotal != null
      ? t('results.foundNarrowed', { total, all: unnarrowedTotal.toLocaleString() })
      : results.took_ms
        ? t('results.foundWithTime', { total, time: results.took_ms })
        : t('results.found', { total });
  const countLabel = (
    <Typography variant="body2" color="text.secondary" dangerouslySetInnerHTML={{ __html: countHtml }} />
  );
  const limitedChip =
    results.acl_status === 'limited' ? (
      <Tooltip title={t('results.limited.tooltip')} arrow placement="bottom-start">
        <Chip
          size="small"
          variant="outlined"
          color="warning"
          icon={<InfoOutlinedIcon fontSize="small" />}
          label={t('results.limited.label')}
          aria-label={t('results.limited.tooltip')}
          sx={{
            // Subtle M3 tonal feel: low emphasis, soft border, no shouting colour.
            borderColor: 'divider',
            bgcolor: (theme) => theme.palette.action.hover,
            color: 'text.secondary',
            '& .MuiChip-icon': { color: 'warning.main' },
            fontWeight: 500,
          }}
        />
      </Tooltip>
    ) : null;
  const header = advanced ? (
    <QuerySyntaxSummary
      query={results.query}
      onSearch={onSearchQuery}
      onEditQuery={onEditQuery}
      lead={countLabel}
      trail={limitedChip}
    />
  ) : (
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
      {countLabel}
      {limitedChip}
    </Box>
  );

  // No results: the conditions stay visible, so they can still be removed.
  if (results.results.length === 0) {
    return (
      <>
        {advanced && header}
        <StatusMessage event="no-results" />
      </>
    );
  }

  // Helper for translated strings passed to ResultCard
  const untitledText = t('results.untitled');
  const openInNewTabText = t('results.openInNewTab');
  const ticketNumberText = (number: string) => t('ticket.number', { number });

  // Show results
  if (results) {
    return (
      <Box>
        {header}
        {phraseHint && onSearchQuery && phraseHint.query === results.query && unnarrowedTotal == null && (
          <PhraseHint phrase={phraseHint.phrase} count={phraseHint.count} onSearch={onSearchQuery} />
        )}

        {/* Results List */}
        <Stack spacing={0} >
          {results.results.map((result, index) => (
            <ResultCard 
              key={result.id} 
              result={result} 
              position={results.from + index + 1}
              searchTerm={results.query}
              untitledText={untitledText}
              openInNewTabText={openInNewTabText}
              ticketNumberText={ticketNumberText}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  return null;
}
